// ============================================================
// RegistrationProcess.jsx
//
// Displays the 4-step registration process as a horizontal
// row of branded cards. Used inside SoftwareService.jsx
// under the "Hands-On Training" section.
//
// Steps:
//   1. Make your application
//   2. Reply to our sent email
//   3. Make your payment
//   4. Get your login credentials
// ============================================================

import React from "react";
import { Box, Typography, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";

// ── MUI Icons (replacing react-icons) ────────────────────────
import EditNoteIcon from "@mui/icons-material/EditNote";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import PaymentIcon from "@mui/icons-material/Payment";
import LoginIcon from "@mui/icons-material/Login";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// ============================================================
//  CONSTANTS
// ============================================================

/**
 * Brand colors — consistent with the rest of the platform.
 */
const BRAND = {
    dark:   "#100720",
    gold:   "#E5C95F",
    yellow: "#FFF19B",
    white:  "#FFFFFF",
};

/**
 * STEPS
 * The four registration steps shown as cards.
 * Each step has a number, icon, title, and description.
 */
const STEPS = [
    {
        number: "01",
        icon:   <EditNoteIcon sx={{ fontSize: 36 }} />,
        title:  "Make Your Application",
        desc:   "Fill out the application form with your details and select your preferred plan.",
    },
    {
        number: "02",
        icon:   <MarkEmailReadIcon sx={{ fontSize: 36 }} />,
        title:  "Reply to Our Email",
        desc:   "Check your inbox and reply to the confirmation email we send you after applying.",
    },
    {
        number: "03",
        icon:   <PaymentIcon sx={{ fontSize: 36 }} />,
        title:  "Make Your Payment",
        desc:   "Complete the payment for your selected plan to confirm your enrollment.",
    },
    {
        number: "04",
        icon:   <LoginIcon sx={{ fontSize: 36 }} />,
        title:  "Get Login Credentials",
        desc:   "Receive your Course Hub access credentials from the admin and start learning.",
    },
];

// ============================================================
//  STYLED COMPONENTS
// ============================================================

/**
 * StepCard
 * Gold branded card for each registration step.
 * Lifts slightly on hover.
 */
const StepCard = styled(Box)({
    backgroundColor: BRAND.gold,
    borderRadius: "16px",
    padding: "28px 20px",
    border: "1px solid rgba(229,201,95,0.4)",
    textAlign: "center",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    "&:hover": {
        transform: "translateY(-6px)",
        boxShadow: "0 12px 32px rgba(16,7,32,0.12)",
    },
});

/**
 * StepIconCircle
 * Dark circular container for each step's icon.
 */
const StepIconCircle = styled(Box)({
    width: 72,
    height: 72,
    borderRadius: "50%",
    backgroundColor: BRAND.dark,
    color: BRAND.gold,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
});

/**
 * StepLabel
 * Dark pill badge showing the step description text.
 */
const StepLabel = styled(Box)({
    backgroundColor: BRAND.dark,
    color: BRAND.white,
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "0.75rem",
    fontWeight: 700,
    display: "inline-block",
    lineHeight: 1.4,
});

// ============================================================
//  MAIN COMPONENT
// ============================================================

export default function RegistrationProcess() {
    return (
        <Box sx={{ width: "100%", maxWidth: "1100px", margin: "0 auto", py: 2 }}>
            <Grid container spacing={2.5} alignItems="stretch">
                {STEPS.map((step, index) => (
                    <Grid item xs={12} sm={6} md={3} key={step.number}>
                        <StepCard>

                            {/* Step number */}
                            <Typography
                                sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 900,
                                    color: BRAND.dark,
                                    opacity: 0.5,
                                    letterSpacing: "2px",
                                    textTransform: "uppercase",
                                }}
                            >
                                STEP {step.number}
                            </Typography>

                            {/* Icon circle */}
                            <StepIconCircle>
                                {step.icon}
                            </StepIconCircle>

                            {/* Step title */}
                            <Typography
                                variant="subtitle2"
                                fontWeight={800}
                                color={BRAND.dark}
                                sx={{ lineHeight: 1.3 }}
                            >
                                {step.title}
                            </Typography>

                            {/* Step description */}
                            <Typography
                                variant="caption"
                                color={BRAND.dark}
                                sx={{ opacity: 0.7, lineHeight: 1.6, fontSize: "0.75rem" }}
                            >
                                {step.desc}
                            </Typography>

                            {/* Step label badge */}
                            <StepLabel>{step.title}</StepLabel>

                        </StepCard>

                        {/* Arrow connector between cards — hidden on last card and mobile */}
                        {index < STEPS.length - 1 && (
                            <Box
                                sx={{
                                    display: { xs: "none", md: "flex" },
                                    position: "absolute",
                                    right: "-14px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: BRAND.dark,
                                    opacity: 0.3,
                                    zIndex: 1,
                                }}
                            >
                                <ArrowForwardIcon fontSize="small" />
                            </Box>
                        )}

                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}
